import { CITIES } from "./constants.js";
import { SERVICE_PAGES } from "./servicePages.js";

export const SITE_URL = "https://monserrurierbreton.fr";

export const travelTime = (d) => {
  if (!d) return "5 à 10 min";
  const lo = Math.max(10, Math.round((d * 1.1 + 5) / 5) * 5);
  return `${lo} à ${lo + (d < 10 ? 5 : 10)} min`;
};

export const cityPath = (c) => `/serrurier-${c.slug}`;
export const serviceCityPath = (s, c) => `/${s.slug}-${c.slug}`;

export const findNeighbors = (city) =>
  (city.neighbors || []).map((s) => CITIES.find((c) => c.slug === s)).filter(Boolean);

export const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

const faqLd = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

const branchLd = (city, path) => ({
  "@context": "https://schema.org",
  "@type": "Locksmith",
  "@id": `${SITE_URL}${cityPath(city)}#business`,
  name: `Mon Serrurier Breton — ${city.name}`,
  image: `${SITE_URL}/og-image.jpg`,
  url: `${SITE_URL}${cityPath(city)}`,
  telephone: "+33780939300",
  priceRange: "€€",
  parentOrganization: { "@id": `${SITE_URL}/#organization` },
  branchOf: { "@id": `${SITE_URL}/#business` },
  address: {
    "@type": "PostalAddress",
    streetAddress: city.name,
    addressLocality: city.name,
    postalCode: city.postal,
    addressRegion: city.departement,
    addressCountry: "FR",
  },
  areaServed: { "@type": "City", name: city.name },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "00:00",
    closes: "23:59",
  },
});

export const citySeo = (city) => {
  const path = cityPath(city);
  const time = travelTime(city.distance);
  const faq = [
    {
      q: `Combien de temps pour intervenir à ${city.name} ?`,
      a: `Depuis notre base de Mont-Dol (35120), ${city.name} est à environ ${city.distance === 0 ? "0 km — intervention immédiate" : `${city.distance} km`}. Comptez ${time} pour notre arrivée sur la commune.`,
    },
    {
      q: `Intervenez-vous la nuit et le week-end à ${city.name} ?`,
      a: `Oui, nous intervenons à ${city.name} 24h/24 et 7j/7, y compris la nuit, les week-ends et jours fériés. Les majorations applicables (+30 % entre 18h30-21h, +50 % après 21h ou les dimanches/fériés) sont annoncées avant l'intervention.`,
    },
    {
      q: `Quels tarifs pour un serrurier à ${city.name} ?`,
      a: `Nos tarifs publics : ouverture porte claquée 100 €, porte fermée à clé 130 €, porte blindée 150-250 €, déplacement forfait 50 €, main-d'œuvre 65-85 €/h. Tous nos tarifs serrurier ${city.name} sont annoncés et confirmés avant intervention.`,
    },
  ];
  return {
    path,
    time,
    faq,
    title: `Serrurier ${city.name} ${city.postal} — Dépannage urgence 24h/24`,
    description: `Serrurier à ${city.name} (${city.postal}) : ouverture de porte sans casse, changement de serrure, blindage, dépannage urgence 24h/24. Artisan local basé à Mont-Dol, devis gratuit, tarifs publics.`,
    jsonLd: [
      branchLd(city, path),
      faqLd(faq),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: "Zones d'intervention", path: "/zones-intervention" },
        { name: city.name, path },
      ]),
    ],
  };
};

export const serviceCitySeo = (service, city) => {
  const path = serviceCityPath(service, city);
  const ctx = { time: travelTime(city.distance), dist: city.distance };
  const faq = service.faq(city, ctx);
  return {
    path,
    ...ctx,
    faq,
    title: service.title(city),
    description: service.description(city, ctx),
    intro: service.intro(city, ctx),
    sections: service.sections(city, ctx),
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `${service.name} à ${city.name}`,
        serviceType: service.name,
        url: `${SITE_URL}${path}`,
        provider: { "@id": `${SITE_URL}${cityPath(city)}#business` },
        areaServed: { "@type": "City", name: city.name },
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: service.fromPrice,
          description: `À partir de ${service.fromPrice} €`,
        },
      },
      branchLd(city),
      faqLd(faq),
      breadcrumb([
        { name: "Accueil", path: "/" },
        { name: `Serrurier ${city.name}`, path: cityPath(city) },
        { name: service.name, path },
      ]),
    ],
  };
};

export const SERVICE_CITY_ROUTES = SERVICE_PAGES.flatMap((s) =>
  CITIES.map((c) => ({ service: s, city: c, path: serviceCityPath(s, c) })),
);
